import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Camera, Mic, Speaker, Loader2, Video, Info } from 'lucide-react';

import { isBrowserAndSystemValid } from '@/utils/browser.utils';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { logError } from '@/utils/logger.utils';
import posthog from 'posthog-js';

export const LobbyPage = (props: {
	startAudioVideoStream: (
		cameraId?: string,
		microphoneId?: string
	) => Promise<MediaStream | null>;
	stopAudioVideoStream: () => boolean;
	audioVideoStream: MediaStream | null;
	setScreenStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
	setDevices: (devices: {
		speakerId: string | null;
		cameraId: string | null;
		microphoneId: string | null;
	}) => void;
}) => {
	const navigate = useNavigate();

	const [isCheckInProgress, setIsCheckInProgress] = useState(false);
	const [isStartingInterview, setIsStartingInterview] = useState(false);
	const [devices, setDevices] = useState<{
		cameras: MediaDeviceInfo[];
		microphones: MediaDeviceInfo[];
		speakers: MediaDeviceInfo[];
	}>({
		cameras: [],
		microphones: [],
		speakers: [],
	});
	const [selectedCamera, setSelectedCamera] = useState<MediaDeviceInfo | null>(
		null
	);
	const [selectedMic, setSelectedMic] = useState<MediaDeviceInfo | null>(null);
	const [selectedSpeaker, setSelectedSpeaker] =
		useState<MediaDeviceInfo | null>(null);
	const [videoPreview, setVideoPreview] = useState<HTMLVideoElement | null>(
		null
	);
	const [isDeviceSetupComplete, setIsDeviceSetupComplete] = useState(false);
	const [browserCheckFailed, setBrowserCheckFailed] = useState(false);

	const detectScreens = async (): Promise<number> => {
		if (!('getScreenDetails' in window)) {
			throw new Error(
				'Screen detection is not supported in this browser. Please try again.'
			);
		}

		try {
			const screens = await (
				window as {
					getScreenDetails: () => Promise<{ screens: { length: number }[] }>;
				}
			).getScreenDetails();

			return screens.screens.length;
		} catch (err) {
			logError(err);

			return 0;
		}
	};

	const handleStartScreenSharing = async (): Promise<boolean> => {
		try {
			const tempScreenCount = await detectScreens();

			if (!tempScreenCount) {
				toast('Error', {
					description:
						'We are unable to detect any screens. This could be a browser issue. We recommend using Chrome, latest version.',
					id: 'screen-detection-error-no-screens',
					duration: 10000,
				});

				return false;
			}

			if (tempScreenCount > 1) {
				toast('Error', {
					description:
						'Multiple screens detected. Please disconnect any secondary displays and ensure only your primary screen is connected.',
					id: 'screen-detection-error-multiple-screens',
					duration: 10000,
				});

				return false;
			}

			const stream = await navigator.mediaDevices.getDisplayMedia({
				video: { displaySurface: 'monitor' },
			});

			const videoTrack = stream.getVideoTracks()[0];
			const settings = videoTrack.getSettings();

			if (settings.displaySurface !== 'monitor') {
				stream.getTracks().forEach((track) => track.stop());

				toast('Error', {
					description:
						'You have not shared your entire screen. Please restart screen sharing and select your entire screen to continue.',
					id: 'screen-detection-error-not-shared',
					duration: 10000,
				});

				return false;
			}

			props.setScreenStream(stream);

			return true;
		} catch (err) {
			logError(err);

			return false;
		}
	};

	const handleStartInterview = async () => {
		try {
			posthog.capture('LobbyPage_StartInterviewClicked');

			const screenShareResponse = await handleStartScreenSharing();

			if (!screenShareResponse) {
				return;
			}

			setIsStartingInterview(true);

			navigate('/interview');
		} catch (err) {
			logError(err);
			toast('Error', {
				description:
					'Could not start the interview. Please refresh and try again.',
				id: 'interview-error',
			});

			setIsStartingInterview(false);

			// Clean up in case of error
			props.stopAudioVideoStream();
		}
	};

	useEffect(() => {
		if (
			isDeviceSetupComplete &&
			selectedCamera &&
			props.audioVideoStream &&
			videoPreview
		) {
			videoPreview.srcObject = props.audioVideoStream;
			videoPreview.play().catch((err) => {
				if (
					err.name === 'AbortError' &&
					err.message.includes('The play() request was interrupted by a call')
				) {
					console.error('Video play interrupted:', err);
				} else {
					logError(err);
				}
			});
		}

		return () => {
			if (videoPreview && videoPreview.srcObject) {
				videoPreview.pause();
				videoPreview.srcObject = null;
			}
		};
	}, [
		isDeviceSetupComplete,
		selectedCamera,
		props.audioVideoStream,
		videoPreview,
	]);

	useEffect(() => {
		setupDevices();

		// dont do cleanup of streams because we dont want to end them when the user is redirected to the interview page.
	}, []);

	const setupDevices = async () => {
		setIsCheckInProgress(true);

		try {
			// Validate browser first
			const browserCheck = isBrowserAndSystemValid();
			if (!browserCheck) {
				posthog.capture('LobbyPage_BrowserCheckFailed');
				setBrowserCheckFailed(true);
				setIsCheckInProgress(false);
				return;
			}

			// Start media streams for camera/mic only
			const mediaStream = await props.startAudioVideoStream();

			if (!mediaStream) {
				toast('Device Error', {
					description:
						'Please allow camera and microphone access to join the interview.',
					id: 'device-error',
				});
				setIsCheckInProgress(false);
				return;
			}

			// Enumerate available devices
			const deviceList = await navigator.mediaDevices.enumerateDevices();

			const cameras = deviceList.filter(
				(device) => device.kind === 'videoinput'
			);
			const microphones = deviceList.filter(
				(device) => device.kind === 'audioinput'
			);
			const speakers = deviceList.filter(
				(device) => device.kind === 'audiooutput'
			);

			// Set devices and select defaults
			setDevices({ cameras, microphones, speakers });
			setSelectedCamera(cameras[0] ?? null);
			setSelectedMic(microphones[0] ?? null);
			setSelectedSpeaker(speakers[0] ?? null);

			setIsDeviceSetupComplete(true);
			setIsCheckInProgress(false);
		} catch (err) {
			logError(err);

			toast('Device Error', {
				description:
					'We were unable to detect your camera and microphone. Please make sure that the devices are connected and we have the necessary permissions to use them.',
				id: 'device-error',
				duration: 10000,
			});

			setIsCheckInProgress(false);
		}
	};

	useEffect(() => {
		const handleDeviceChange = async () => {
			props.stopAudioVideoStream();
			const streams = await props.startAudioVideoStream(
				selectedCamera?.deviceId ?? undefined,
				selectedMic?.deviceId ?? undefined
			);

			if (!streams) {
				toast('Device Error', {
					description:
						'We were unable to detect your camera and microphone. Please make sure that the devices are connected and we have the necessary permissions to use them.',
					id: 'device-error',
					duration: 10000,
				});

				return;
			}

			props.setDevices({
				speakerId: selectedSpeaker?.deviceId ?? null,
				cameraId: selectedCamera?.deviceId ?? null,
				microphoneId: selectedMic?.deviceId ?? null,
			});
		};

		handleDeviceChange();
	}, [selectedCamera, selectedMic, selectedSpeaker]);

	if (browserCheckFailed) {
		return (
			<div className="min-h-screen bg-blue-50 flex flex-col items-center mt-20 py-8 px-4">
				<div className="w-full max-w-4xl flex flex-col items-center">
					<h1 className="text-2xl font-semibold text-gray-800 mb-6">
						Browser Check Failed
					</h1>
					<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 w-full max-w-md text-center">
						<div className="bg-amber-100 text-amber-700 p-3 rounded-lg mb-4 inline-flex mx-auto">
							<Info className="h-6 w-6" />
						</div>
						<h2 className="text-xl font-medium text-gray-800 mb-3">
							Browser Check Failed
						</h2>
						<p className="text-gray-600 mb-6">
							Organizations prefer interviews conducted from a desktop browser.
							We have shared a link with you in the email to join the call.
						</p>
						<Button
							className="bg-blue-600 hover:bg-blue-700 text-white"
							onClick={() => navigate('/')}
						>
							Return to Home
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-blue-50 flex flex-col items-center py-8 px-4">
			<div className="w-full max-w-4xl">
				<div className="text-center mb-6">
					<h1 className="text-2xl font-semibold text-gray-800">
						Join Your Interview
					</h1>
				</div>

				{/* Main content container with white background */}
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					{/* Top section with video and device settings */}
					<div className="grid md:grid-cols-12 gap-0">
						{/* Left side - Video preview */}
						<div className="md:col-span-7 border-r border-gray-200">
							<div className="bg-gray-100 py-2 px-4 border-b border-gray-200 flex justify-between items-center">
								<h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
									<Video className="h-4 w-4 text-blue-500" />
									Camera Preview
								</h3>
								{props.audioVideoStream && (
									<span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
										Live
									</span>
								)}
							</div>

							<div className="aspect-video bg-gray-50 flex items-center justify-center relative overflow-hidden">
								{(isCheckInProgress || isStartingInterview) && (
									<div className="absolute inset-0 flex items-center justify-center bg-white/75 z-10">
										<div className="text-center">
											<Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-3" />
											<p className="text-gray-700 font-medium">
												{isStartingInterview
													? 'Preparing interview...'
													: 'Setting up devices...'}
											</p>
										</div>
									</div>
								)}

								{props.audioVideoStream ? (
									<div className="w-full h-full">
										<video
											ref={(el) => setVideoPreview(el)}
											className="w-full h-full object-cover"
											muted
											playsInline
										/>
									</div>
								) : (
									<div className="text-gray-400 flex flex-col items-center p-6">
										<Camera className="h-10 w-10 mb-2 opacity-50" />
										<p>Camera preview unavailable</p>
									</div>
								)}
							</div>
						</div>

						{/* Right side - Device settings */}
						<div className="md:col-span-5">
							<div className="p-4 border-b border-gray-200 bg-gray-100">
								<h3 className="text-sm font-medium text-gray-700">
									Device Settings
								</h3>
							</div>
							<div className="p-5 space-y-4">
								<div className="space-y-2">
									<label className="text-sm font-medium flex items-center gap-2 text-gray-700">
										<Camera className="w-4 h-4 text-gray-600" />
										Camera
									</label>
									<Select
										value={selectedCamera?.deviceId || ''}
										onValueChange={(value) =>
											setSelectedCamera(
												devices.cameras.find(
													(device) => device.deviceId === value
												) ?? null
											)
										}
										disabled={isCheckInProgress || isStartingInterview}
									>
										<SelectTrigger className="w-full bg-white border-gray-200">
											<SelectValue placeholder="Select camera" />
										</SelectTrigger>
										<SelectContent>
											{devices.cameras.map((device) => (
												<SelectItem
													key={device.deviceId}
													value={device.deviceId}
												>
													{device.label ||
														`Camera ${device.deviceId.substring(0, 5)}`}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<label className="text-sm font-medium flex items-center gap-2 text-gray-700">
										<Mic className="w-4 h-4 text-gray-600" />
										Microphone
									</label>
									<Select
										value={selectedMic?.deviceId || ''}
										onValueChange={(value) =>
											setSelectedMic(
												devices.microphones.find(
													(device) => device.deviceId === value
												) ?? null
											)
										}
										disabled={isCheckInProgress || isStartingInterview}
									>
										<SelectTrigger className="w-full bg-white border-gray-200">
											<SelectValue placeholder="Select microphone" />
										</SelectTrigger>
										<SelectContent>
											{devices.microphones.map((device) => (
												<SelectItem
													key={device.deviceId}
													value={device.deviceId}
												>
													{device.label ||
														`Mic ${device.deviceId.substring(0, 5)}`}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<label className="text-sm font-medium flex items-center gap-2 text-gray-700">
										<Speaker className="w-4 h-4 text-gray-600" />
										Audio Output
									</label>
									<Select
										value={selectedSpeaker?.deviceId || ''}
										onValueChange={(value) =>
											setSelectedSpeaker(
												devices.speakers.find(
													(device) => device.deviceId === value
												) ?? null
											)
										}
										disabled={isCheckInProgress || isStartingInterview}
									>
										<SelectTrigger className="w-full bg-white border-gray-200">
											<SelectValue placeholder="Select speaker" />
										</SelectTrigger>
										<SelectContent>
											{devices.speakers.map((device) => (
												<SelectItem
													key={device.deviceId}
													value={device.deviceId}
												>
													{device.label ||
														`Speaker ${device.deviceId.substring(0, 5)}`}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
							<div>
								<div className="px-5 pb-5">
									<Button
										className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2 cursor-pointer"
										size="lg"
										disabled={
											isCheckInProgress ||
											isStartingInterview ||
											!isDeviceSetupComplete
										}
										onClick={handleStartInterview}
									>
										<Video className="h-5 w-5" />
										Share Screen and Start Interview
									</Button>
								</div>
							</div>
						</div>
					</div>

					{/* Bottom section with tips */}
					<div className="border-t border-gray-200 p-4 bg-gray-50">
						<div className="flex gap-3">
							<Info className="h-5 w-5 text-gray-600 shrink-0 mt-0.5" />
							<div>
								<p className="font-medium text-gray-800 mb-2">
									Screen Sharing Requirements:
								</p>
								<p className="text-sm text-gray-600 mb-3">
									When you click the "Share Screen and Start Interview" button,
									you will be prompted to share your screen.
									<br />
									Please disconnect any secondary displays and ensure you select
									your entire screen when prompted.
								</p>
								<div className="text-sm text-gray-600">
									<p className="font-medium text-gray-800 mb-1">
										You may encounter errors if:
									</p>
									<ul className="list-disc pl-5 space-y-1 text-gray-600">
										<li>Multiple screens are connected.</li>
										<li>You do not share your entire screen.</li>
									</ul>
								</div>
							</div>
						</div>
						<div className="flex gap-3 mt-4">
							<Info className="h-5 w-5 text-gray-600 shrink-0 mt-0.5" />
							<div>
								<p className="font-medium text-gray-800 mb-2">
									About this interview:
								</p>
								<p className="text-sm text-gray-600 mb-3">
									This is a conversation to get to know you better. We'll
									discuss your experience, skills, and preferences so we can
									connect you with organizations that are best fit for you.
								</p>
								<div className="text-sm text-gray-600">
									<p className="font-medium text-gray-800 mb-1">Quick Tips:</p>
									<ul className="list-disc pl-5 space-y-1 text-gray-600">
										<li>Ensure your camera and microphone are working</li>
										<li>
											We request you to not switch tabs or windows during your
											interview
										</li>
										<li>Please ensure you have a stable internet connection</li>
									</ul>
								</div>
								<p className="text-xs text-gray-500 mt-3">
									Your camera, microphone, and screen will be shared during this
									session. Please close any private or sensitive information
									before joining.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
